import * as React from 'react';

import { cn } from '../lib/utils';

/**
 * Named layout compositions (docs/latest-analysis-ui-ux.md UX-06).
 *
 * The measured defect was NOT token drift — adoption of the kit is excellent
 * (3,774 `<Button>` against 29 raw `<button>`, 27 hex literals, zero hardcoded
 * font-family). Every view simply RE-ASSEMBLED the same spacing, density and
 * typography by hand: 6,287 `className` strings over 40 characters across 1,959
 * module views, of which only 2,276 are distinct. Same paints, different
 * brushstrokes.
 *
 * The head of that distribution is not noise, it is five recurring ideas:
 *
 *   DataLabel     536 sites — the uppercase muted caption over a value
 *   SectionTitle   241 sites — an icon + heading row
 *   Toolbar        167 sites — the bordered header strip of a panel
 *   ViewShell      126 sites — a view's scrolling column
 *   MetricValue    ~320 sites — a mono / tabular / muted readout
 *
 * <b>Every variant below emits the exact class string the views already wrote.</b>
 * `layout.identity.test.tsx` asserts that byte-for-byte against the literals
 * measured in the tree. That is deliberate: it makes adoption a pure refactor —
 * identical DOM, identical pixels — so the visual-regression baselines and DOM
 * snapshots cannot drift while ~1,000 call sites move. A primitive that
 * "cleaned up" the classes on the way through would force every one of those
 * baselines to be re-blessed, and a re-blessed baseline proves nothing.
 *
 * Extra classes passed via `className` are appended, so a site with one local
 * deviation still adopts the primitive instead of forking it.
 */

type DivProps = React.HTMLAttributes<HTMLDivElement>;

// ─────────────────────────────────────────────────────────────────────────────
// DataLabel
// ─────────────────────────────────────────────────────────────────────────────

export interface FieldLabelProps extends React.HTMLAttributes<HTMLElement> {
  /** Lay out as a row so an icon can sit beside the text. */
  inline?: boolean;
  /** Render as the bordered header strip of a panel (implies `inline`). */
  bordered?: boolean;
  /** Add the 3-unit bottom margin used when the label heads a block. */
  spaced?: boolean;
  /** `xs` is the default caption size; `2xs` is the denser table variant. */
  size?: 'xs' | '2xs';
  /** Monospace, for identifiers and codes rather than prose. */
  mono?: boolean;
  /** Element to render as. Defaults to `div`. */
  as?: 'div' | 'span' | 'p' | 'h3' | 'h4';
}

/**
 * The uppercase muted caption that names a value — the single most repeated
 * composition in the product (536 sites across its variants).
 */
export const DataLabel = React.forwardRef<HTMLElement, FieldLabelProps>(
  ({ inline, bordered, spaced, size = 'xs', mono, as = 'div', className, ...rest }, ref) => {
    const Tag = as as React.ElementType;
    return (
      <Tag
        ref={ref}
        className={cn(
          spaced && 'mb-3',
          (inline || bordered) && 'flex items-center gap-1.5',
          bordered && 'border-b border-border px-3 py-2',
          size === '2xs' ? 'text-[10px]' : 'text-xs',
          'uppercase tracking-wide text-muted-foreground',
          mono && 'font-mono',
          className,
        )}
        {...rest}
      />
    );
  },
);
DataLabel.displayName = 'DataLabel';

// ─────────────────────────────────────────────────────────────────────────────
// SectionTitle
// ─────────────────────────────────────────────────────────────────────────────

export interface SectionTitleProps extends React.HTMLAttributes<HTMLElement> {
  /** `sm` is the default panel heading; `base` heads a whole section. */
  size?: 'sm' | 'base';
  /** `semibold` (default) or the lighter `medium` used for sub-headings. */
  weight?: 'semibold' | 'medium';
  /** Add the 3-unit bottom margin used when the title heads a block. */
  spaced?: boolean;
  as?: 'div' | 'h2' | 'h3' | 'h4';
}

/** An icon-and-text heading row — 241 sites across its weight/size variants. */
export const SectionTitle = React.forwardRef<HTMLElement, SectionTitleProps>(
  ({ size = 'sm', weight = 'semibold', spaced, as = 'div', className, ...rest }, ref) => {
    const Tag = as as React.ElementType;
    return (
      <Tag
        ref={ref}
        className={cn(
          spaced && 'mb-3',
          'flex items-center gap-2',
          size === 'base' ? 'text-base' : 'text-sm',
          weight === 'medium' ? 'font-medium' : 'font-semibold',
          className,
        )}
        {...rest}
      />
    );
  },
);
SectionTitle.displayName = 'SectionTitle';

// ─────────────────────────────────────────────────────────────────────────────
// Toolbar
// ─────────────────────────────────────────────────────────────────────────────

export interface ToolbarProps extends DivProps {
  /** Push trailing content to the far edge and allow wrapping. */
  spread?: boolean;
  /** `md` is the standard header strip; `sm` is the denser sub-panel strip. */
  density?: 'md' | 'sm';
  /** Omit the bottom rule (for a toolbar that is not a panel header). */
  borderless?: boolean;
}

/**
 * The bordered header strip of a panel — 167 sites. `spread` is the variant
 * that carries actions on the right; it also wraps, which is what keeps a dense
 * toolbar from overflowing its container at 1280×720 (see UX-07).
 */
export const Toolbar = React.forwardRef<HTMLDivElement, ToolbarProps>(
  ({ spread, density = 'md', borderless, className, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex',
        spread && 'flex-wrap',
        'items-center',
        spread && 'justify-between',
        'gap-2',
        !borderless && 'border-b border-border',
        !borderless && (density === 'sm' ? 'px-3 py-2' : 'px-4 py-3'),
        className,
      )}
      {...rest}
    />
  ),
);
Toolbar.displayName = 'Toolbar';

// ─────────────────────────────────────────────────────────────────────────────
// ViewShell
// ─────────────────────────────────────────────────────────────────────────────

export interface ViewShellProps extends DivProps {
  /** `6` is the standard view inset; `4` is the denser console inset. */
  pad?: 4 | 6;
  /** `y` scrolls vertically only (the common case); `both` scrolls either way. */
  scroll?: 'y' | 'both';
}

/**
 * A view's full-height scrolling column — 126 sites. Owning the overflow here
 * rather than per-view is what stops a wide table pushing the whole body
 * sideways.
 */
export const ViewShell = React.forwardRef<HTMLDivElement, ViewShellProps>(
  ({ pad = 6, scroll = 'both', className, ...rest }, ref) => (
    <div
      ref={ref}
      // Spelled out rather than composed as `p-${pad}`: Tailwind's scanner reads
      // source text, so an interpolated utility name is never emitted into the
      // stylesheet and the padding would silently vanish.
      className={cn(
        'flex h-full flex-col gap-4',
        scroll === 'both' && 'p-6 overflow-auto',
        scroll === 'y' && 'overflow-y-auto',
        scroll === 'y' && (pad === 4 ? 'p-4' : 'p-6'),
        className,
      )}
      {...rest}
    />
  ),
);
ViewShell.displayName = 'ViewShell';

// ─────────────────────────────────────────────────────────────────────────────
// MetricValue
// ─────────────────────────────────────────────────────────────────────────────

export interface MetricValueProps extends React.HTMLAttributes<HTMLElement> {
  /** Monospace — for identifiers, hashes and codes. */
  mono?: boolean;
  /** Lining figures that do not jitter as a value ticks. Default true. */
  tabular?: boolean;
  /** Right-align, for a numeric table column. */
  align?: 'left' | 'right';
  /** Full-strength text instead of the muted default. */
  emphasis?: 'muted' | 'default';
  as?: 'span' | 'div' | 'td';
}

/**
 * A readout: the number or identifier under a {@link DataLabel}. ~320 sites.
 * `tabular` defaults on because these are overwhelmingly live values, and
 * proportional digits make a ticking readout shimmer.
 */
export const MetricValue = React.forwardRef<HTMLElement, MetricValueProps>(
  (
    { mono, tabular = true, align = 'left', emphasis = 'muted', as = 'span', className, ...rest },
    ref,
  ) => {
    const Tag = as as React.ElementType;
    return (
      <Tag
        ref={ref}
        className={cn(
          align === 'right' && 'text-right',
          mono && 'font-mono',
          'text-xs',
          tabular && 'tabular-nums',
          emphasis === 'muted' && 'text-muted-foreground',
          className,
        )}
        {...rest}
      />
    );
  },
);
MetricValue.displayName = 'MetricValue';
